"use client";

import { useState } from "react";

type JsonTreeProps = {
  value: unknown;
  name?: string;
  depth?: number;
  defaultExpanded?: boolean;
};

function valuePreview(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") {
    const trimmed = value.length > 48 ? `${value.slice(0, 48)}…` : value;
    return JSON.stringify(trimmed);
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (typeof value === "object") return `Object(${Object.keys(value as object).length})`;
  return String(value);
}

function valueColor(value: unknown): string {
  if (value === null || value === undefined) return "var(--ink-faint)";
  if (typeof value === "string") return "var(--coral)";
  if (typeof value === "number") return "#74c0fc";
  if (typeof value === "boolean") return "#ffd166";
  return "var(--ink-body)";
}

export function JsonTree({ value, name, depth = 0, defaultExpanded }: JsonTreeProps) {
  const expandedByDefault = defaultExpanded ?? depth < 2;
  const [expanded, setExpanded] = useState(expandedByDefault);

  const isBranch = value !== null && value !== undefined && typeof value === "object";

  if (!isBranch) {
    return (
      <div className="flex min-w-0 items-baseline gap-1 py-0.5 font-mono text-[10px] leading-snug">
        {name !== undefined && (
          <span className="shrink-0" style={{ color: "var(--ink-faint)" }}>
            {name}:
          </span>
        )}
        <span className="min-w-0 break-all" style={{ color: valueColor(value) }}>
          {valuePreview(value)}
        </span>
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item] as const)
    : Object.entries(value as Record<string, unknown>);

  const branchLabel = Array.isArray(value) ? `[${value.length}]` : `{${entries.length}}`;

  return (
    <div className="min-w-0">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full min-w-0 items-baseline gap-1 py-0.5 text-left font-mono text-[10px] leading-snug hover:opacity-80"
      >
        <span className="w-3 shrink-0" style={{ color: "var(--ink-faint)" }}>
          {expanded ? "▾" : "▸"}
        </span>
        {name !== undefined && (
          <span className="shrink-0" style={{ color: "var(--ink-faint)" }}>
            {name}:
          </span>
        )}
        <span style={{ color: "var(--ink-body)" }}>{branchLabel}</span>
      </button>
      {expanded && (
        <div
          className="min-w-0 border-l pl-2"
          style={{ borderColor: "var(--hairline)", marginLeft: "0.35rem" }}
        >
          {entries.map(([key, child]) => (
            <JsonTree
              key={key}
              name={key}
              value={child}
              depth={depth + 1}
              defaultExpanded={depth + 1 < 2}
            />
          ))}
        </div>
      )}
    </div>
  );
}
